import { useMemo, useState } from 'react';

const SOURCE_FILES = [
  {
    id: 'bank',
    title: 'Estado de cuenta bancario',
    description: 'Archivo emitido por el banco con depósitos, retiros y referencias del periodo.',
    accept: '.xlsx,.xls,.csv,.pdf',
    required: true
  },
  {
    id: 'accounting',
    title: 'Auxiliar contable SAFRAN',
    description: 'Movimientos contables exportados del auxiliar para la cuenta seleccionada.',
    accept: '.xlsx,.xls,.csv',
    required: true
  },
  {
    id: 'opening',
    title: 'Partidas pendientes iniciales',
    description: 'Movimientos en tránsito que vienen de una conciliación anterior.',
    accept: '.xlsx,.xls,.csv',
    required: false
  },
  {
    id: 'evidence',
    title: 'Evidencia soporte',
    description: 'Comprobantes o documentos de respaldo para diferencias y revisión posterior.',
    accept: '.xlsx,.xls,.csv,.pdf,.zip',
    required: false
  }
];

const PROCESS_STEPS = [
  {
    title: 'Carga documental',
    detail: 'Seleccionar archivos del banco, SAFRAN y pendientes del periodo anterior.',
    state: 'available'
  },
  {
    title: 'Normalización',
    detail: 'Fecha, monto, referencia, descripción y contraparte se prepararán para comparación.',
    state: 'planned'
  },
  {
    title: 'Matching automático',
    detail: 'Se priorizarán coincidencias exactas y se sugerirán agrupaciones para revisión.',
    state: 'planned'
  },
  {
    title: 'Cierre y auditoría',
    detail: 'El contador aprobará excepciones y conservará el historial mensual en Airtable.',
    state: 'planned'
  }
];

const MOCK_REVIEW_ITEMS = [
  {
    label: 'Match exacto 1:1',
    detail: 'Mismo monto, fecha y referencia',
    status: 'Auto-match propuesto'
  },
  {
    label: 'Agrupación N:1',
    detail: 'Varios depósitos contra una partida SAFRAN',
    status: 'Revisión requerida'
  },
  {
    label: 'Descripción ambigua',
    detail: 'Agente IA sugerirá contraparte y referencia',
    status: 'Pendiente'
  }
];

function getFileSummary(file) {
  if (!file) return 'Ningún archivo seleccionado';
  const sizeKb = Math.max(1, Math.round(file.size / 1024));
  return `${file.name} · ${sizeKb.toLocaleString('es-MX')} KB`;
}

function ConciliacionBancariaView() {
  const [context, setContext] = useState({
    company: 'SAFRAN',
    account: 'SAFRAN_MXP',
    period: '2026-05'
  });
  const [selectedFiles, setSelectedFiles] = useState({});

  const requiredReady = useMemo(
    () => SOURCE_FILES.filter(source => source.required).every(source => selectedFiles[source.id]),
    [selectedFiles]
  );
  const selectedCount = Object.values(selectedFiles).filter(Boolean).length;

  const handleContextChange = event => {
    const { name, value } = event.target;
    setContext(previous => ({ ...previous, [name]: value }));
  };

  const handleFileChange = (sourceId, event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFiles(previous => ({ ...previous, [sourceId]: file }));
  };

  return (
    <section className="conciliation-view" data-ui-view="conciliation" data-ui-section="conciliation-view">
      <div className="conciliation-hero">
        <div>
          <p className="eyebrow">SAFRAN · Conciliación bancaria</p>
          <h2>Preparar conciliación mensual</h2>
          <p className="subtitle">
            Carga la documentación del periodo para revisar posteriormente coincidencias y partidas pendientes.
            El historial se conservará en Airtable cuando la automatización sea habilitada.
          </p>
        </div>
        <div className="conciliation-mode-card" aria-label="Estado del prototipo">
          <span className="conciliation-status-pill planned">Diseño visual</span>
          <strong>Sin envío de información</strong>
          <small>Los archivos seleccionados permanecen en este navegador.</small>
        </div>
      </div>

      <div className="conciliation-layout">
        <div className="conciliation-main">
          <article className="conciliation-panel">
            <div className="conciliation-panel-head">
              <div>
                <p className="conciliation-kicker">Paso 1</p>
                <h3>Identifica la cuenta y el periodo</h3>
              </div>
              <span className="conciliation-status-pill available">Disponible</span>
            </div>
            <div className="conciliation-context-grid">
              <label>
                Empresa
                <select name="company" value={context.company} onChange={handleContextChange}>
                  <option value="SAFRAN">SAFRAN</option>
                </select>
              </label>
              <label>
                Cuenta bancaria
                <select name="account" value={context.account} onChange={handleContextChange}>
                  <option value="SAFRAN_MXP">SAFRAN MXP</option>
                  <option value="SAFRAN_USD">SAFRAN USD</option>
                </select>
              </label>
              <label>
                Periodo
                <input type="month" name="period" value={context.period} onChange={handleContextChange} />
              </label>
            </div>
          </article>

          <article className="conciliation-panel">
            <div className="conciliation-panel-head">
              <div>
                <p className="conciliation-kicker">Paso 2</p>
                <h3>Carga los archivos fuente</h3>
                <p>Los dos primeros documentos son necesarios para iniciar una conciliación.</p>
              </div>
              <span className={`conciliation-status-pill ${requiredReady ? 'ready' : 'planned'}`}>
                {requiredReady ? 'Documentos listos' : 'Pendiente de selección'}
              </span>
            </div>

            <div className="conciliation-upload-grid">
              {SOURCE_FILES.map(source => {
                const file = selectedFiles[source.id];
                return (
                  <label
                    key={source.id}
                    className={`conciliation-upload-card ${file ? 'selected' : ''}`}
                    htmlFor={`conciliation-file-${source.id}`}
                  >
                    <div className="conciliation-upload-title">
                      <strong>{source.title}</strong>
                      <span className={source.required ? 'required' : 'optional'}>
                        {source.required ? 'Obligatorio' : 'Opcional'}
                      </span>
                    </div>
                    <p>{source.description}</p>
                    <input
                      id={`conciliation-file-${source.id}`}
                      type="file"
                      accept={source.accept}
                      onChange={event => handleFileChange(source.id, event)}
                    />
                    <span className="conciliation-file-summary">{getFileSummary(file)}</span>
                  </label>
                );
              })}
            </div>

            <div className="conciliation-actions">
              <p>
                {selectedCount
                  ? `${selectedCount} archivo${selectedCount === 1 ? '' : 's'} seleccionado${selectedCount === 1 ? '' : 's'} para vista previa.`
                  : 'Selecciona archivos para revisar nombres y preparar la carga.'}
              </p>
              <button type="button" className="btn primary" disabled title="Disponible cuando se configure el workflow n8n">
                Procesar archivos
              </button>
            </div>
          </article>
        </div>

        <aside className="conciliation-side">
          <article className="conciliation-panel conciliation-summary">
            <p className="conciliation-kicker">Flujo previsto</p>
            <h3>{context.company} · {context.account.replace('_', ' ')}</h3>
            <p className="conciliation-period">{context.period || 'Periodo sin definir'}</p>
            <ol className="conciliation-step-list">
              {PROCESS_STEPS.map(step => (
                <li key={step.title}>
                  <span className={`conciliation-step-dot ${step.state}`} />
                  <div>
                    <strong>{step.title}</strong>
                    <small>{step.detail}</small>
                  </div>
                </li>
              ))}
            </ol>
          </article>

          <article className="conciliation-panel conciliation-review-preview">
            <div className="conciliation-panel-head compact">
              <div>
                <p className="conciliation-kicker">Vista previa</p>
                <h3>Revisión de matches</h3>
              </div>
              <span className="conciliation-status-pill planned">Próximamente</span>
            </div>
            <p className="conciliation-preview-note">
              Los agentes propondrán coincidencias; las agrupaciones siempre requerirán confirmación del contador.
            </p>
            <div className="conciliation-review-list">
              {MOCK_REVIEW_ITEMS.map(item => (
                <div className="conciliation-review-item" key={item.label}>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                  <span>{item.status}</span>
                </div>
              ))}
            </div>
          </article>
        </aside>
      </div>
    </section>
  );
}

export default ConciliacionBancariaView;
